import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { collectionKeys } from "@/lib/query-keys";
import type { PaginatedResponse, ReportCollection } from "@/types/api";

const PAGE_SIZE = 100;
export const COLLECTION_POLL_INTERVAL_MS = 3_000;
/**
 * Stop hammering after 10 min of one export sitting in PENDING/PROCESSING.
 *
 * A worker that dies mid-job leaves the export PENDING server-side forever,
 * and with no deadline the page re-requests a 100-row list every 3s for as
 * long as the tab holds focus. Ten minutes is roughly double the worst
 * realistic export: `useReportProgress` estimates 0.33s per photo, so even a
 * thousand-photo report lands near five and a half minutes.
 */
export const COLLECTION_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * export id → epoch ms when we first saw it running. Module-level on purpose:
 * refetchInterval callbacks run per query instance and must share the give-up
 * clock across remounts. Keyed on the EXPORT, not the collection, so a
 * re-export re-arms the window for free — it mints a new id, which has no
 * clock yet. (`tilePollInterval` keys on the plan and therefore needs an
 * explicit `resetTilePollTimer` on its retile path; this one does not.)
 */
const collectionPollFirstSeen = new Map<string, number>();

export function __resetCollectionPollStateForTesting(): void {
  collectionPollFirstSeen.clear();
}

// Resume on window focus: clearing the clocks re-arms every timed-out export.
// `refetchIntervalInBackground: false` already parks the timer while the tab
// is away, but wall-clock time keeps running — without this, a tab left longer
// than the window would come back to a permanently frozen bar. Mirrors
// `use-project-plans.ts`, HMR hygiene included.
if (typeof window !== "undefined") {
  const onFocus = () => collectionPollFirstSeen.clear();
  window.addEventListener("focus", onFocus);
  import.meta.hot?.dispose(() => window.removeEventListener("focus", onFocus));
}

/**
 * The refetchInterval policy. Deterministic but NOT pure — it seeds and prunes
 * the module-level give-up clocks for the collections it is shown; extracted
 * from the hook so unit tests can drive it directly (mirrors `tilePollInterval`
 * in `plans-view/use-project-plans.ts`).
 *
 * The list itself is the poll: its payload already carries every collection's
 * latest export, so one request refreshes every running row at once. Polling
 * each row's own detail endpoint instead — what the pre-collections list did —
 * would put a project with ten exports in flight ten times the load.
 */
export function collectionPollInterval(
  collections: ReportCollection[] | undefined,
): number | false {
  if (!collections?.length) return false;
  const now = Date.now();
  let runningCount = 0;
  let anyInsideWindow = false;
  for (const collection of collections) {
    const latest = collection.latest_export;
    if (!latest) continue;
    const isRunning =
      latest.status === "PENDING" || latest.status === "PROCESSING";
    if (!isRunning) {
      // Evidence this export settled — drop its clock so the map does not grow
      // across a long session. Scoped to the exports THIS query can see: a
      // whole-map prune would wipe the clocks of any other active collections
      // query (two projects open at once) and disable their give-up forever.
      collectionPollFirstSeen.delete(latest.id);
      continue;
    }
    runningCount += 1;
    const firstSeen = collectionPollFirstSeen.get(latest.id) ?? now;
    if (!collectionPollFirstSeen.has(latest.id)) {
      collectionPollFirstSeen.set(latest.id, now);
    }
    if (now - firstSeen <= COLLECTION_POLL_TIMEOUT_MS) anyInsideWindow = true;
  }
  if (runningCount === 0) return false;
  return anyInsideWindow ? COLLECTION_POLL_INTERVAL_MS : false;
}

/**
 * The Reports page's list. It lists collections, not files — each row is a
 * report you can come back to, carrying the state of its most recent export.
 */
export function useProjectCollections(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useInfiniteQuery({
    queryKey: collectionKeys.list(projectId),
    queryFn: ({ pageParam }) =>
      fetchWithAuth<PaginatedResponse<ReportCollection>>(
        `/reports/3.0/projects/${projectId}/collections/?page=${pageParam}&page_size=${PAGE_SIZE}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    select: (data) => ({
      collections: data.pages.flatMap((page) => page.results),
      totalCount: data.pages[0]?.count ?? 0,
    }),
    staleTime: 30_000,
    // NB: receives raw InfiniteData (select doesn't apply here).
    refetchInterval: (query) =>
      collectionPollInterval(
        query.state.data?.pages.flatMap((page) => page.results),
      ),
    // Nobody is watching a bar move in a backgrounded tab.
    refetchIntervalInBackground: false,
  });
}
